import { Button, Modal, SearchInput, Tooltip } from '@orbita-ui/core';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Card } from 'shared/components';
import { PageTitle } from 'shared/components/Fonts';
import { useDataFetcher } from 'shared/http';
import { InputPlaceholder, TablePlaceholder } from 'shared/placeholders';
import { useNavigate } from 'react-router-dom';
import { Table } from 'shared/table';
import { Usuario } from 'user/models/Usuario';
import { useUserService } from 'user/services/useUserService';
import { CREATION_USERS_PATH, DETAIL_USERS_PATH, EDIT_USERS_PATH } from 'user/routes';
import { BaseModal, useModal } from 'shared/modal';

export function UsersListPage() {
  const userService = useUserService();
  const [userList, setRequest] = useDataFetcher<Usuario[]>();
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { showModal, closeModal, openModal } = useModal(false);

  const fetchUsers = useCallback(
    async (query = '') => {
      setErrorMessage(null);
      setRequest(async () => {
        try {
          const response = await userService.getUsers(query);
          return response.body;
        } catch (e) {
          setErrorMessage('Hubo un error al obtener la lista de usuarios');
        }
      });
    },
    [setRequest, userService],
  );

  useEffect(() => {
    if (searchInput === '') {
      fetchUsers(searchInput);
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      fetchUsers(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchInput, fetchUsers]);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleDelete = useCallback(async () => {
    if (!selectedUser) return;
    try {
      await userService.deleteUser(selectedUser);
      fetchUsers();
      closeModal();
    } catch (e) {
      setErrorMessage('Hubo un error al eliminar el usuario');
    }
  }, [selectedUser, fetchUsers, closeModal, userService]);

  return (
    <>
      <Modal show={showModal} onClose={closeModal}>
        <BaseModal
          state={errorMessage ? 'error' : undefined}
          title={errorMessage ? 'Error' : 'Eliminar Usuario'}
          body={<p>{errorMessage ?? '¿Desea eliminar al usuario?'}</p>}
          footer={
            <>
              <Button
                className="bg-secondary"
                color="inverted"
                variant="ghost"
                onClick={closeModal}
              >
                Cancelar
              </Button>
              {!errorMessage && (
                <Button color="destructive" onClick={handleDelete}>
                  Eliminar
                </Button>
              )}
            </>
          }
        />
      </Modal>

      <Container fluid>
        <Row className="mb-3 d-flex justify-content-between align-items-center">
          <Col>
            <PageTitle>Lista de Usuarios</PageTitle>
          </Col>
          <Col className="text-end">
            <Button
              color="primary"
              icon="AddIcon"
              type="button"
              onClick={() => navigate(CREATION_USERS_PATH)}
            >
              Crear Usuario
            </Button>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card>
              <Card.Title>Usuarios</Card.Title>
              <Card.Body>
                <div className="p-3">
                  {userList.isLoading ? (
                    <InputPlaceholder />
                  ) : (
                    <SearchInput
                      placeholder="Buscar usuario"
                      helperText="Ingrese el código de usuario, el detalle o el número de cuenta controlada"
                      fullWidth
                      value={searchInput}
                      onChange={handleSearchChange}
                    />
                  )}
                </div>
                <Table
                  thead={
                    <tr>
                      <td>Código de usuario</td>
                      <td>Detalle</td>
                      <td>Cuenta controlada</td>
                      <td></td>
                    </tr>
                  }
                  tbody={
                    <>
                      {userList.isLoading ? (
                        <TablePlaceholder cols={4} rows={5} />
                      ) : (
                        userList.data?.map((row) => (
                          <tr key={row.username}>
                            <td>{row.username}</td>
                            <td>{row.detalle}</td>
                            <td>{row.recursoControlado.numeroCuenta}</td>
                            <td className="text-end">
                              <Tooltip text="Información de usuario" position="left">
                                <Button
                                  onClick={() =>
                                    navigate(
                                      DETAIL_USERS_PATH.replace(':userId', `${row.id}`),
                                    )
                                  }
                                  className="bg-secondary"
                                  color="inverted"
                                  variant="ghost"
                                  icon="InfoIcon"
                                  size="small"
                                />
                              </Tooltip>
                              {!row.cuentaRaiz && (
                                <>
                                  <Tooltip text="Editar usuario" position="left">
                                    <Button
                                      onClick={() =>
                                        navigate(
                                          EDIT_USERS_PATH.replace(':userId', `${row.id}`),
                                        )
                                      }
                                      color="inverted"
                                      icon="EditIcon"
                                      size="small"
                                    />
                                  </Tooltip>
                                  <Tooltip text="Eliminar usuario" position="left">
                                    <Button
                                      color="destructive"
                                      icon="DeleteIcon"
                                      size="small"
                                      onClick={() => {
                                        setSelectedUser(row);
                                        openModal();
                                      }}
                                    />
                                  </Tooltip>
                                </>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </>
                  }
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
